import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { IoIosArrowBack } from 'react-icons/io';
import { formatDuration } from '@/features/details/date-pick/chip';
import { DatePickerContext } from '@/features/details/date-pick/context';
import { VSpace } from '@/components/spacing';
import { BigButton } from '@/components/big-button';
import { breakpoints } from '@/components/responsive';

type Props = { close: () => void };
export const CustomDatePicker = React.forwardRef(({ close }: Props, ref) => {
  const { ranges, setRanges, setVersion, quickSelect } =
    useContext(DatePickerContext);
  const [localRanges, setLocalRanges] = useState(ranges);

  const selectLocalRanges = (r) => {
    setVersion(undefined);
    setLocalRanges([r.rollup]);
  };

  const onBack = () => {
    quickSelect();
  };

  const onApply = () => {
    setVersion(undefined);
    setRanges(localRanges);
    close();
    quickSelect();
  };
  return (
    <Container ref={ref}>
      <BackBtn onClick={onBack} title="Custom" showRange />
      <Picker
        showDateDisplay={false}
        onChange={selectLocalRanges}
        ranges={localRanges}
      />
      <VSpace value={3} />
      <BigButton
        onClick={onApply}
        background="blue"
        noTransform
        size="medium"
        fillWidth
      >
        Apply
      </BigButton>
      <VSpace />
    </Container>
  );
});

export const Picker: typeof DateRange = styled(DateRange)`
  && {
    .rdrCalendarWrapper {
      border-radius: 1rem;
    }
  }
`;

const Container = styled.div`
  box-shadow: 0 22px 154px 0 rgba(0, 0, 0, 0.17),
    0 18px 46px 0 rgba(177, 217, 203, 0.18), 0 8px 24px 0 rgba(0, 0, 0, 0.12);
  background-color: white;

  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  border-radius: 1rem;
  padding: 1rem 2rem;

  ${breakpoints.down(`md`)} {
    width: 100%;
    border-radius: 2rem 2rem 0 0;
  }
`;

const Back = styled.button`
  width: 100%;
  font-family: 'JetBrains Mono', serif;
  font-weight: 500;
  border-bottom: 2px solid #acaeb0;
  padding-bottom: 1rem;

  user-select: none;

  span:first-child {
    :hover {
      cursor: pointer;
      opacity: 0.5;
    }

    :active {
      cursor: default;
      opacity: 1;
    }

    display: flex;
    justify-content: start;
    align-items: center;
  }

  span:nth-child(2) {
    margin-left: 3rem;
    font-weight: 400;
    font-size: 12px;
  }

  display: flex;
  justify-content: start;
  align-items: center;
  font-size: 15px;

  margin: 0;

  svg {
    margin-right: 1rem;
  }
`;

export function BackBtn({
  onClick,
  title,
  showRange,
}: {
  onClick: () => void;
  title: string;
  showRange: boolean;
}) {
  const { ranges } = useContext(DatePickerContext);
  const startDate = ranges?.[0]?.startDate;
  const endDate = ranges?.[0]?.endDate;
  return (
    <Back type="button" onClick={onClick}>
      <span>
        <IoIosArrowBack />
        {title}
      </span>
      {showRange && <span>{formatDuration(startDate, endDate)}</span>}
    </Back>
  );
}
